/**
 * ADD INSTRUCTION — REDESIGN (Karan 26/10/2026; design "Add instruction —
 * redesign", pages 3–7, and the motion of the draft opening in the table).
 *
 * Priced work opens as a full-width DRAFT inside the project's Instructions
 * table, under the rows it joins (page 3):
 *
 *   I3 [Additional steel beam and padstones…]                 Draft  ✓ Saved 16:02
 *   Where it came from        │ Fee  £60/h · …   [Add from the catalogue…] ✦ Draft with AI
 *   ○ A won enquiry           │ ITEM … NO CX HOURS FEE
 *   ● The client asked directly│ …
 *   ○ Price it later          │ TOTAL  How fees are calculated        9:14  £460
 *   Instructed by    Change   │ Past projects like this │ Fee to the client
 *   [the email, the ask lit]  │ [range, median, table]   │ £ 460   = calculated
 *   Lead        Due  Optional │
 *   £460 · 9:14 budget · starts Not started once confirmed   Discard draft · Preview fee letter · Confirm instruction 3
 *
 * The quick cases stay small (page 4): a won enquiry whose quoted lines come
 * across as quoted, or "price it later" — a modal. "Client asked directly"
 * and "Open as a draft" open the full draft.
 *
 * Edit instruction opens its row the same way (decision 2), with the status
 * (forward only) and the lead as today.
 *
 * Saves as you go (projects/{pid}/instructionDrafts/{uid}); Confirm goes to
 * createInstruction on the server, which numbers it in a transaction and does
 * the money again. Nothing here trusts its own arithmetic for the record.
 */
import * as FC from '../fee-calc.js';
import { buildCatalogue, keyOf } from '../fee-catalogue.js';
import { mountFeeCalculator } from './fee-calculator.js';
import { candidateEnquiries, quotedLines, quotedRevisions, instructionLines, requestSentence } from '../instruction-model.js';
export { candidateEnquiries } from '../instruction-model.js';
import { escapeHtml, hoursHM, money, toastV4 } from '../ui.js';
import { orgSetting } from '../org.js';
import { openDialog } from './dialog.js';
import {
  listCatalogue, saveCatalogue, allEnquiries, callFunction, generateFeeDraft, feeDraftForInstruction,
  createInstructionServer, updateInstructionCalc, getInstructionDraft, saveInstructionDraft, deleteInstructionDraft, resumeIfHeld,
} from '../db.js';

export const STATUS_ORDER = ['Not Started', 'In Progress', 'Review', 'Ready to Issue', 'Part Issued', 'Issued', 'Complete'];
const H = (h) => hoursHM(Number(h) || 0);
const rateNow = () => Number(orgSetting('chargeOutRate', 60)) || 60;
const tail = (r) => String(r || '').split('/').pop() || '';
const reduceMotion = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { return false; } };

// ── the catalogue, read once per page load ──────────────────────────────────
let catP = null;
export function loadCatalogue() {
  if (!catP) {
    catP = Promise.all([
      listCatalogue('catalogueItems').catch(() => []),
      listCatalogue('catalogueServices').catch(() => []),
      allEnquiries().catch(() => []),
    ]).then(([items, services, enquiries]) => {
      const svc = new Set((services || []).map((s) => s && s.name).filter(Boolean));
      for (const it of items || []) if (it && it.service) svc.add(String(it.service));
      return { entries: buildCatalogue({ items: items || [], enquiries: enquiries || [] }), services: [...svc].sort(), enquiries: enquiries || [] };
    });
    catP.catch(() => { catP = null; });
  }
  return catP;
}

function snippetHtml(text, ask) {
  const t = String(text || '');
  if (!ask) return escapeHtml(t);
  const at = t.replace(/\s+/g, ' ').indexOf(ask);
  const flat = t.replace(/\s+/g, ' ');
  if (at < 0) return escapeHtml(flat);
  return `${escapeHtml(flat.slice(0, at))}<mark class="icd-ask">${escapeHtml(ask)}</mark>${escapeHtml(flat.slice(at + ask.length))}`;
}
const whenText = (d) => {
  const x = d ? new Date(d) : null;
  if (!x || Number.isNaN(x.getTime())) return '';
  return `${x.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, ${x.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};
const dayText = (d) => { const x = d ? new Date(d) : null; return x && !Number.isNaN(x.getTime()) ? x.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : ''; };
const entryToBy = (en) => {
  if (!en) return null;
  const snippet = String(en.snippet || en.body || '').slice(0, 600);
  return { name: en.senderName || null, email: en.email || null, threadId: en.threadId || null, messageId: en.messageId || null,
    subject: en.subject || null, at: en.date ? new Date(en.date).toISOString() : null, quote: requestSentence(snippet) || snippet.slice(0, 300) || null, snippet };
};
const byToEntry = (by) => by && ({ key: by.threadId || by.subject || 'x', threadId: by.threadId || '', subject: by.subject || '(no subject)',
  from: by.email ? `${by.name || ''} <${by.email}>` : (by.name || ''), senderName: by.name || '', email: by.email || '', to: '', dir: 'Received',
  snippet: by.snippet || by.quote || '', body: '', date: by.at ? new Date(by.at) : null, msgCount: 1, isManual: false, attachments: null });

// ── Past projects like this (page 3) ────────────────────────────────────────
export function pastHtml(r, { budget = 0, msg = '' } = {}) {
  const head = (sub) => `<div class="icd-past-h"><b>Past projects like this</b>${sub ? ` <span>· ${escapeHtml(sub)}</span>` : ''}</div>`;
  if (!r) return `${head('')}<p class="icd-past-msg">${escapeHtml(msg)}</p>`;
  const v = r.verdict || {};
  const comp = (r.matches || []).filter((m) => m.finished && m.loggedHours != null && !(m.addedLater || []).length);
  const rng = v.range;
  let bar = '', sentence = '';
  if (rng && !v.lowConfidence) {
    const lo = Math.min(rng.low, budget || rng.low), hi = Math.max(rng.high, budget || rng.high);
    const pad = (hi - lo) * 0.12 || 1;
    const x = (h) => `${Math.max(0, Math.min(100, ((h - (lo - pad)) / ((hi + pad) - (lo - pad))) * 100)).toFixed(1)}%`;
    bar = `<div class="icd-range" aria-hidden="true"><span class="icd-range-band" style="left:${x(rng.low)};right:calc(100% - ${x(rng.high)})"></span><span class="icd-range-med" style="left:${x(rng.median)}"></span>${budget ? `<span class="icd-range-dot" style="left:${x(budget)}"></span>` : ''}</div>`;
    const where = !budget ? '' : budget < rng.low ? ` <span class="icd-under">This budget, <b>${H(budget)}</b>, is under the range: similar projects took longer.</span>`
      : budget > rng.high ? ` This budget, <b>${H(budget)}</b>, is over the range.` : ` This budget, <b>${H(budget)}</b>, sits inside the range.`;
    const rates = comp.filter((m) => m.quotedFee && m.loggedHours).map((m) => m.quotedFee / m.loggedHours);
    const realised = rates.length ? ` Realised rate on them: £${Math.round(Math.min(...rates))}–£${Math.round(Math.max(...rates))}/h.` : '';
    sentence = `<p class="icd-past-s">They took ${H(rng.low)}–${H(rng.high)}, median <b>${H(rng.median)}</b>.${where}${realised}</p>`;
  } else {
    sentence = `<p class="icd-past-s">${v.comparable ? `Only ${v.comparable} comparable finished project${v.comparable === 1 ? '' : 's'}` : 'No comparable finished project'} — no confident range${v.checker ? `; worth ${escapeHtml(v.checker)}’s check` : ''}.</p>`;
  }
  const rows = (r.matches || []).slice(0, 4).map((m) => {
    const pct = m.loggedHours != null && m.quotedHours ? Math.round(((m.loggedHours - m.quotedHours) / m.quotedHours) * 100) : null;
    return `<div class="icd-past-row"><span>${escapeHtml(String(m.ref || '—'))}</span><span class="icd-past-var">${escapeHtml(m.description || m.title || (m.items || []).map((i) => i.name).slice(0, 2).join(', ') || '—')}</span><span class="r">${escapeHtml(H(m.quotedHours))}</span><span class="r"><b>${m.loggedHours != null ? escapeHtml(H(m.loggedHours)) : '—'}</b></span><span class="r ${pct == null ? '' : pct > 0 ? 'icd-over' : 'icd-ok'}">${pct == null ? '' : `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`}</span></div>`;
  }).join('');
  const table = rows ? `<div class="icd-past-row icd-past-th"><span>Project</span><span>Variation</span><span class="r">Quoted</span><span class="r">Took</span><span></span></div>${rows}` : '<p class="icd-past-msg">No past project was priced with these items closely enough to compare.</p>';
  return `${head(comp.length ? `${comp.length} finished, priced the same way` : '')}${bar}${sentence}${table}`;
}

/**
 * openInstructionDraft(opts) — the full draft, inside the Instructions table.
 * opts: host, project, projectId, session, mode ('add'|'edit'), seq, instr,
 *   users [{id,name}], defaultLead, candidates, prefill, emailsP, openEmail,
 *   onDone(), onClose()
 */
export async function openInstructionDraft(opts) {
  const { host, project, projectId, session, mode = 'add', seq, instr = null, users = [], candidates = [], emailsP = Promise.resolve([]), openEmail } = opts;
  if (!host) return null;
  const existing = host.querySelector('.icd');
  if (existing) { existing.scrollIntoView({ block: 'nearest', behavior: reduceMotion() ? 'auto' : 'smooth' }); return null; }
  const edit = mode === 'edit';
  const rate = rateNow();
  const resume = edit ? null : (opts.resume !== undefined ? opts.resume : await getInstructionDraft(projectId).catch(() => null));
  const pre = { ...(resume || {}), ...(opts.prefill || {}) };

  // ── state ──
  const st = {
    description: edit ? (instr.description || '') : (pre.description || ''),
    kind: edit ? ((instr.source && instr.source.kind) || null) : (pre.kind || (candidates.length ? 'enquiry' : 'direct')),
    enquiryId: edit ? tail(instr.enquiryRef) : (pre.enquiryId || (candidates[0] && candidates[0].id) || null),
    instructedBy: edit ? (instr.instructedBy || null) : (pre.instructedBy !== undefined ? pre.instructedBy : null),
    lead: edit ? tail(instr.lead) : (pre.lead !== undefined ? pre.lead : (opts.defaultLead || '')),
    due: edit ? (instr.due || '') : (pre.due || ''),
    status: edit ? (instr.status || 'Not Started') : 'Not Started',
    feeTouched: false, feeToClient: null, feeReason: edit ? (instr.feeReason || '') : (pre.feeReason || ''),
    savedAt: resume && resume.updatedAt ? resume.updatedAt : null,
    entries: [], picking: false,
  };
  const startLines = edit ? instructionLines(instr) : (pre.lines || []);
  const startRev = edit ? ((instr.feeCalc && instr.feeCalc.revisions) || { count: 0, percent: 20, charged: false })
    : (pre.revisions || { count: 1, percent: 20, charged: false });
  if (edit && instr.fee != null) {
    const calc = FC.calcTotalFee(startLines, rate) + FC.revFee(startLines, startRev, rate);
    if (!startLines.length || Number(instr.fee) !== calc) { st.feeTouched = true; st.feeToClient = Number(instr.fee); }
  } else if (!edit && pre.feeToClient != null) { st.feeTouched = true; st.feeToClient = Number(pre.feeToClient); }
  const candById = new Map(candidates.map((e) => [e.id, e]));

  // ── the frame ──
  const box = document.createElement('div');
  box.className = 'icd' + (reduceMotion() ? '' : ' icd-enter');
  box.setAttribute('role', 'region');
  box.setAttribute('aria-label', edit ? `Edit instruction ${seq}` : `New instruction ${seq}`);
  box.innerHTML = `
    <div class="icd-head">
      <span class="icd-n">I${escapeHtml(String(seq))}</span>
      <input class="icd-desc" type="text" maxlength="500" placeholder="What has been instructed?" aria-label="What has been instructed" value="${escapeHtml(st.description)}">
      <span class="icd-pill">${edit ? 'Editing' : 'Draft'}</span>
      <span class="icd-saved" aria-live="polite"></span>
    </div>
    <div class="icd-body">
      <div class="icd-left"></div>
      <div class="icd-right">
        <div class="icd-card icd-fee"></div>
        <div class="icd-card icd-below">
          <div class="icd-past"></div>
          <div class="icd-ftc"></div>
        </div>
      </div>
    </div>
    <div class="icd-foot">
      <span class="icd-sum"></span>
      <span class="icd-foot-r">
        <button type="button" class="icd-discard">${edit ? 'Cancel' : 'Discard draft'}</button>
        ${edit ? '' : '<button type="button" class="btn icd-letter" disabled title="Fee letter coming soon">Preview fee letter</button>'}
        <button type="button" class="btn primary icd-confirm"></button>
      </span>
    </div>
    <p class="icd-err" role="alert" hidden></p>`;
  if (edit && opts.row) { opts.row.after(box); opts.row.hidden = true; } else host.appendChild(box);
  // Full width while it is open (page 3): the overview drops to one column.
  const ov = box.closest('.pd4-ov');
  if (ov) ov.classList.add('is-drafting');
  const $ = (s) => box.querySelector(s);
  const left = $('.icd-left'), feeHost = $('.icd-fee'), pastHost = $('.icd-past'), ftcHost = $('.icd-ftc');
  const descIn = $('.icd-desc');

  // ── left column ──
  const userOpt = (sel) => `<option value="">— Unassigned —</option>${users.map((u) => `<option value="${escapeHtml(u.id)}"${u.id === sel ? ' selected' : ''}>${escapeHtml(u.name)}</option>`).join('')}`;
  function drawLeft() {
    const c = candById.get(st.enquiryId) || candidates[0] || null;
    const srcHtml = edit ? `<div class="icd-f"><span class="icd-l">Where it came from</span><span class="icd-src-ro">${escapeHtml(
      st.kind === 'enquiry' ? 'A won enquiry' : st.kind === 'direct' ? 'The client asked directly — a won enquiry opened behind it' : st.kind === 'later' ? 'Priced later' : 'Added before the calculator')}</span></div>`
      : `<fieldset class="icd-f icd-src"><legend class="icd-l">Where it came from</legend>
        <label class="icd-radio${candidates.length ? '' : ' is-off'}"><input type="radio" name="icd-src-${escapeHtml(String(seq))}" value="enquiry"${st.kind === 'enquiry' ? ' checked' : ''}${candidates.length ? '' : ' disabled'}><span><span class="icd-rt">A won enquiry</span><span class="icd-rs">${c ? `${escapeHtml(c.number || 'An enquiry')} is ${c.status === 'Won' ? 'won' : 'open'} for this client` : 'None open for this client'}</span></span></label>
        ${st.kind === 'enquiry' && candidates.length > 1 ? `<select class="f-select icd-enq" aria-label="Which enquiry">${candidates.map((e) => `<option value="${escapeHtml(e.id)}"${e.id === st.enquiryId ? ' selected' : ''}>${escapeHtml([e.number || 'Unnumbered', e.subject || e.description || ''].filter(Boolean).join(' · ').slice(0, 80))}</option>`).join('')}</select>` : ''}
        <label class="icd-radio"><input type="radio" name="icd-src-${escapeHtml(String(seq))}" value="direct"${st.kind === 'direct' ? ' checked' : ''}><span><span class="icd-rt">The client asked directly</span><span class="icd-rs">Track opens a won enquiry behind it</span></span></label>
        <label class="icd-radio"><input type="radio" name="icd-src-${escapeHtml(String(seq))}" value="later"${st.kind === 'later' ? ' checked' : ''}><span><span class="icd-rt">Price it later</span><span class="icd-rs">Saved unpriced, with a task</span></span></label>
      </fieldset>`;
    const by = st.instructedBy;
    const ask = by ? (by.quote && String(by.snippet || '').includes(by.quote) ? by.quote : requestSentence(by.snippet)) : null;
    const card = st.picking ? `<div class="icd-pick" role="listbox" aria-label="This project’s emails">${st.entries.length ? st.entries.slice(0, 12).map((en, i) => `<button type="button" class="icd-pick-row" data-pick="${i}"><span class="icd-pick-a"><b>${escapeHtml(en.senderName || en.email || 'Unknown')}</b><span>${escapeHtml(whenText(en.date))}</span></span><span class="icd-pick-s">${escapeHtml(en.subject || '')}</span></button>`).join('') : '<p class="icd-none">No emails filed to this project yet.</p>'}<button type="button" class="icd-pick-row icd-pick-none" data-pick="-1">None — it wasn’t by email</button></div>`
      : by ? `<div class="icd-by">
          <div class="icd-by-h"><b>${escapeHtml(by.name || by.email || 'Unknown')}</b><span>${escapeHtml(whenText(by.at))}</span></div>
          ${by.subject ? `<div class="icd-by-s">${escapeHtml(by.subject)}</div>` : ''}
          ${by.snippet || by.quote ? `<div class="icd-by-b">${snippetHtml(by.snippet || by.quote, ask)}</div>` : ''}
          ${by.threadId || by.subject ? '<button type="button" class="icd-link icd-open-mail">Open the email</button>' : ''}
        </div>` : `<div class="icd-by icd-by-none">Not linked to an email.${edit ? '' : ' <button type="button" class="icd-link icd-change">Pick the email</button>'}</div>`;
    const status = edit ? `<label class="icd-f"><span class="icd-l">Status</span><select class="f-select icd-status">${STATUS_ORDER.slice(Math.max(0, STATUS_ORDER.indexOf(st.status))).map((s) => `<option${s === st.status ? ' selected' : ''}>${escapeHtml(s)}</option>`).join('')}</select></label>` : '';
    left.innerHTML = `${srcHtml}
      <div class="icd-f"><div class="icd-lh"><span class="icd-l">Instructed by</span>${edit ? '' : `<button type="button" class="icd-link icd-change">${st.picking ? 'Close' : 'Change'}</button>`}</div>${card}</div>
      ${status}
      <div class="icd-ld">
        <label class="icd-f"><span class="icd-l">Lead</span><select class="f-select icd-lead">${userOpt(st.lead)}</select></label>
        <label class="icd-f"><span class="icd-lh"><span class="icd-l">Due</span><span class="icd-opt">Optional</span></span><input class="f-input icd-due" type="date" value="${escapeHtml(st.due || '')}"></label>
      </div>`;
    left.querySelectorAll('input[type=radio]').forEach((r) => r.addEventListener('change', () => { setKind(r.value); }));
    left.querySelector('.icd-enq')?.addEventListener('change', (e) => { st.enquiryId = e.target.value; loadEnquiryLines(true); drawLeft(); dirty(); });
    left.querySelectorAll('.icd-change').forEach((b) => b.addEventListener('click', () => { st.picking = !st.picking; drawLeft(); }));
    left.querySelectorAll('[data-pick]').forEach((b) => b.addEventListener('click', () => {
      const i = +b.dataset.pick; st.instructedBy = i < 0 ? null : entryToBy(st.entries[i]); st.picking = false; drawLeft(); dirty();
    }));
    left.querySelector('.icd-open-mail')?.addEventListener('click', () => { if (openEmail) openEmail(st.entryOpen || byToEntry(st.instructedBy)); });
    left.querySelector('.icd-lead').addEventListener('change', (e) => { st.lead = e.target.value; dirty(); drawFoot(); });
    left.querySelector('.icd-due').addEventListener('change', (e) => { st.due = e.target.value; dirty(); });
    left.querySelector('.icd-status')?.addEventListener('change', (e) => { st.status = e.target.value; });
  }
  function setKind(k) {
    st.kind = k;
    if (k === 'enquiry' && !st.enquiryId && candidates[0]) st.enquiryId = candidates[0].id;
    if (k === 'enquiry') loadEnquiryLines(false);
    drawLeft(); drawFee(); dirty();
  }
  function loadEnquiryLines(replace) {
    const e = candById.get(st.enquiryId);
    if (!e || !calc) return;
    const cur = calc.state();
    if (cur.lines.length && !replace) return;
    calc.setLines(quotedLines(e), quotedRevisions(e));
  }

  // ── the calculator ──
  let calc = null;
  const cat = await loadCatalogue().catch(() => ({ entries: [], services: [] }));
  function drawFee() {
    const later = st.kind === 'later';
    feeHost.classList.toggle('is-later', later);
    let laterNote = feeHost.querySelector('.icd-later');
    if (later && !laterNote) {
      laterNote = document.createElement('p'); laterNote.className = 'icd-later';
      feeHost.prepend(laterNote);
    }
    if (laterNote) {
      laterNote.hidden = !later;
      const leadName = (users.find((u) => u.id === st.lead) || {}).name;
      laterNote.textContent = `Saved unpriced. Track adds a task for ${leadName || 'you'} to price instruction ${seq}; any lines below are kept in the draft, not on the instruction.`;
    }
  }
  const calcHost = document.createElement('div');
  feeHost.appendChild(calcHost);
  calc = mountFeeCalculator(calcHost, {
    rate, catalogue: cat.entries, services: cat.services, lines: startLines, revisions: startRev, defaultService: project.service || null,
    canAddToCatalogue: true,
    onAddToCatalogue: async ({ name, service, hours, unit }) => {
      const data = { name, hours, service: service || null, unit: unit || 'each', sub: null,
        updatedBy: session.email || null, updatedAt: new Date().toISOString(), addedFrom: 'fee-calculator' };
      const ref = await saveCatalogue('catalogueItems', null, data);
      return { key: keyOf(name), id: ref && ref.id, name, hours, service: service || null, unit: unit || 'each', sub: null, uses: 0, saved: true };
    },
    onDraftWithAi: async () => {
      const useEnq = st.kind === 'enquiry' && st.enquiryId;
      const desc = descIn.value.trim();
      if (!useEnq && desc.length < 10) throw new Error('Say what has been instructed first — the draft is priced from it.');
      const res = useEnq ? await generateFeeDraft(st.enquiryId) : await feeDraftForInstruction(projectId, desc);
      const d = res && res.draft;
      if (!d || !(d.items || []).length) return { lines: [], note: 'The model found nothing to price in the brief.' };
      return {
        lines: d.items.map((it) => ({ item: it.name, kind: 'work', stdHours: it.hours, qty: it.qty, complexity: 1, source: it.custom ? 'AI-custom' : 'AI', ...(it.custom ? { oneOff: true } : {}) })),
        note: (d.flags || []).length ? `AI draft — check: ${d.flags.slice(0, 3).join(' · ')}` : 'AI draft — the model chose the items; the hours and fees are the catalogue’s.',
      };
    },
    onChange: () => { drawFtc(); drawFoot(); dirty(); schedulePast(); },
  });
  drawFee();

  // ── past projects like this ──
  const past = { key: null, timer: null, r: null, msg: 'Add items to see past projects priced the same way, and the hours they really took.' };
  function schedulePast() {
    const s = calc.state();
    const items = s.lines.filter((l) => !FC.lineIsComm(l) && l.item).map((l) => ({ name: l.item, hours: Math.round(FC.lineEffHours(l) * 10) / 10 }));
    const key = JSON.stringify([items, Math.round(s.hours * 10), Math.round(feeNow())]);
    if (key === past.key) return;
    past.key = key;
    clearTimeout(past.timer);
    if (!items.length) { past.r = null; past.msg = 'Add items to see past projects priced the same way, and the hours they really took.'; drawPast(); calc.setSuggestions([]); return; }
    past.timer = setTimeout(async () => {
      if (!past.r) { past.msg = 'Reading past projects…'; drawPast(); }
      try {
        const r = await callFunction('feeHistory', { projectId, description: descIn.value.trim(), service: project.service || null, items, quoteHours: s.hours || null, quoteFee: feeNow() || null });
        if (past.key !== key) return;
        past.r = r && r.data ? r.data : r;
        drawPast();
        calc.setSuggestions((past.r && past.r.cooccur) || []);
      } catch (err) {
        past.r = null; past.msg = `Couldn’t read past projects — ${(err && err.message) || err}`; drawPast();
      }
    }, 600);
  }
  function drawPast() { pastHost.innerHTML = pastHtml(past.r, { budget: calc.state().hours, msg: past.msg }); }

  // ── fee to the client ──
  const calcFee = () => calc.state().fee;
  const feeNow = () => (st.feeTouched && st.feeToClient != null ? st.feeToClient : calcFee());
  function drawFtc(focus) {
    const s = calc.state();
    const fee = feeNow();
    const g = FC.feeGap(fee, s.fee, s.hours);
    // An instruction priced by hand before the calculator (no lines): its fee
    // stands on its own — there is no calculation to be above or below.
    const byHand = edit && !s.lines.length;
    const same = byHand || !st.feeTouched || g.gap === 0;
    ftcHost.innerHTML = `
      <div class="icd-ftc-h"><b>Fee to the client</b><span>${byHand ? 'set by hand' : same ? '= calculated' : `calculated ${escapeHtml(money(s.fee))}`}</span></div>
      <label class="icd-ftc-in"><span aria-hidden="true">£</span><input type="number" min="0" step="5" inputmode="numeric" aria-label="Fee to the client, £" value="${escapeHtml(String(Math.round(fee)))}"></label>
      ${byHand ? '<p class="icd-ftc-note">Priced before the calculator. Add lines to price it the shared way.</p>' : same ? '<p class="icd-ftc-note">Change it and the gap, the real £/h and a reason come up.</p>'
        : `<p class="icd-ftc-gap${g.below ? ' is-below' : ''}">${escapeHtml(money(Math.abs(g.gap)))} ${g.below ? 'below' : 'above'} the calculation${g.realRate != null ? ` · £${g.realRate}/h on ${escapeHtml(H(s.hours))} budget` : ''}${g.realRate != null && g.realRate < rate ? ` (charge-out £${rate}/h)` : ''}</p>
           <label class="icd-ftc-r"><span class="icd-l">${g.below ? 'Why it’s below the calculation' : 'Why it’s above (optional)'}</span><textarea class="f-textarea" rows="2" maxlength="600" placeholder="${g.below ? 'e.g. agreed with the client on the phone; repeat work' : ''}">${escapeHtml(st.feeReason || '')}</textarea></label>
           <button type="button" class="icd-link icd-ftc-reset">Back to the calculation</button>`}`;
    const inp = ftcHost.querySelector('input');
    inp.addEventListener('change', () => {
      const v = Math.round(Number(inp.value));
      if (!Number.isFinite(v) || v < 0) { drawFtc(); return; }
      st.feeTouched = v !== s.fee; st.feeToClient = st.feeTouched ? v : null;
      drawFtc('reason'); drawFoot(); dirty();
    });
    ftcHost.querySelector('textarea')?.addEventListener('input', (e) => { st.feeReason = e.target.value; drawFoot(); dirty(); });
    ftcHost.querySelector('.icd-ftc-reset')?.addEventListener('click', () => { st.feeTouched = false; st.feeToClient = null; st.feeReason = ''; drawFtc(); drawFoot(); dirty(); });
    if (focus === 'reason') ftcHost.querySelector('textarea')?.focus();
  }

  // ── footer ──
  const confirmBtn = $('.icd-confirm');
  function blocker() {
    const s = calc.state();
    if (!descIn.value.trim()) return 'Say what has been instructed.';
    if (!edit && st.kind === 'enquiry' && !st.enquiryId) return 'Pick the enquiry it came from.';
    if (st.kind !== 'later' && !edit && !s.lines.length) return 'Add a fee line, or choose Price it later.';
    if (st.kind !== 'later' && s.lines.length) {
      const g = FC.feeGap(feeNow(), s.fee, s.hours);
      if (g.below && !String(st.feeReason || '').trim()) return 'The fee to the client is below the calculation — say why.';
    }
    return null;
  }
  function drawFoot() {
    const s = calc.state();
    const later = st.kind === 'later';
    $('.icd-sum').innerHTML = later
      ? `<b>Unpriced</b> · a task to price it goes to ${escapeHtml((users.find((u) => u.id === st.lead) || {}).name || 'you')}${edit ? '' : ' · starts Not started once confirmed'}`
      : `<b>${escapeHtml(money(feeNow()))}</b> · ${escapeHtml(H(edit && !s.lines.length ? Number(instr.budgetHours) || 0 : s.hours))} budget${edit ? '' : ' · starts Not started once confirmed'}`;
    confirmBtn.textContent = edit ? `Save instruction ${seq}` : `Confirm instruction ${seq}`;
    const why = blocker();
    confirmBtn.disabled = !!why;
    confirmBtn.title = why || '';
  }
  descIn.addEventListener('input', () => { st.description = descIn.value; drawFoot(); dirty(); });

  // ── saves as you go ──
  let saveTimer = null;
  const savedEl = $('.icd-saved');
  const showSaved = () => { if (!edit && st.savedAt) savedEl.textContent = `✓ Saved ${new Date(st.savedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`; };
  function snapshot() {
    const s = calc.state();
    return { description: descIn.value, kind: st.kind, enquiryId: st.enquiryId || null, instructedBy: st.instructedBy || null,
      lead: st.lead || '', due: st.due || '', lines: s.lines, revisions: s.revisions,
      feeToClient: st.feeTouched ? st.feeToClient : null, feeReason: st.feeReason || '', seqHint: seq };
  }
  function dirty() {
    if (edit) return;
    clearTimeout(saveTimer);
    savedEl.textContent = 'Saving…';
    saveTimer = setTimeout(async () => {
      try { await saveInstructionDraft(projectId, JSON.parse(JSON.stringify(snapshot()))); st.savedAt = new Date().toISOString(); showSaved(); }
      catch (e) { savedEl.textContent = 'Not saved — check your connection'; }
    }, 800);
  }

  // ── close / confirm ──
  const err = $('.icd-err');
  function collapse(after) {
    clearTimeout(saveTimer);
    const done = () => { box.remove(); if (opts.row) opts.row.hidden = false; if (ov && !ov.querySelector('.icd')) ov.classList.remove('is-drafting'); if (after) after(); };
    if (reduceMotion()) { done(); return; }
    box.classList.add('icd-leave');
    setTimeout(done, 260);
  }
  $('.icd-discard').addEventListener('click', async () => {
    if (edit) { collapse(opts.onClose); return; }
    await deleteInstructionDraft(projectId).catch(() => {});
    collapse(opts.onClose);
  });
  confirmBtn.addEventListener('click', async () => {
    const why = blocker();
    if (why) { err.textContent = why; err.hidden = false; return; }
    err.hidden = true;
    clearTimeout(saveTimer);
    const s = calc.state();
    const payload = {
      projectId, description: descIn.value.trim(), lead: st.lead || null, due: st.due || null,
      lines: st.kind === 'later' ? [] : s.lines.map(({ asQuoted, ...l }) => l), revisions: s.revisions,
      feeToClient: st.kind === 'later' ? null : feeNow(), feeReason: st.feeReason || null,
    };
    confirmBtn.disabled = true; confirmBtn.textContent = edit ? 'Saving…' : 'Confirming…';
    try {
      if (edit) {
        await updateInstructionCalc({ ...payload, seq: Number(seq), status: st.status });
        toastV4(`Instruction ${seq} saved`);
      } else {
        const by = st.instructedBy ? { ...st.instructedBy } : null;
        if (by) delete by.snippet;
        const out = await createInstructionServer({ ...payload, source: { kind: st.kind, enquiryId: st.kind === 'enquiry' ? st.enquiryId : null }, instructedBy: by });
        resumeIfHeld(projectId, session.email, session.displayName, 'an instruction was added').catch(() => {});
        toastV4(out && out.enquiryNumber && st.kind === 'direct' ? `Instruction ${out.seq} added · ${out.enquiryNumber} opened behind it` : `Instruction ${(out && out.seq) || seq} added`);
      }
      collapse(opts.onDone);
    } catch (e) {
      err.textContent = (e && e.message) || String(e); err.hidden = false;
      drawFoot();
    }
  });
  box.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !e.target.closest('.fcx-search')) { e.stopPropagation(); }
  });

  // ── first paint ──
  drawLeft(); drawFtc(); drawFoot(); drawPast(); showSaved(); schedulePast();
  if (!edit && st.kind === 'enquiry' && !startLines.length) loadEnquiryLines(false);
  emailsP.then((rows) => {
    st.entries = (rows || []).filter((en) => en && en.dir !== 'Sent');
    if (st.instructedBy && st.instructedBy.threadId) st.entryOpen = st.entries.find((en) => en.threadId === st.instructedBy.threadId) || null;
    // A new draft takes the newest email from outside as its evidence — shown, with Change beside it.
    if (!edit && !resume && st.instructedBy == null && !(opts.prefill && 'instructedBy' in opts.prefill) && st.entries[0]) {
      st.instructedBy = entryToBy(st.entries[0]); st.entryOpen = st.entries[0];
    }
    drawLeft();
  }).catch(() => {});
  requestAnimationFrame(() => {
    box.scrollIntoView({ block: 'nearest', behavior: reduceMotion() ? 'auto' : 'smooth' });
    if (!descIn.value) descIn.focus();
  });
  return { box, calc };
}

/**
 * The quick cases stay small (page 4): a modal with "A won enquiry | Client
 * asked directly | Price it later". A won enquiry's quoted lines come across
 * marked As quoted; Price it later adds it unpriced with a task. "Client asked
 * directly" and "Open as a draft" hand over to the full draft.
 */
export function openAddInstructionQuick(opts) {
  const { project, projectId, session, seq, users = [], candidates = [], onOpenDraft, onDone } = opts;
  const rate = rateNow();
  let tab = candidates.length ? 'enquiry' : 'later';
  let enqId = candidates[0] ? candidates[0].id : null;
  let lead = opts.defaultLead || '';
  let due = '';
  let desc = '';
  const candById = new Map(candidates.map((e) => [e.id, e]));
  const descFor = (e) => (e ? String(e.description || e.subject || e.brief || '').replace(/\s+/g, ' ').trim().slice(0, 200) : '');
  desc = descFor(candById.get(enqId));
  let d = null;
  const quote = () => {
    const e = candById.get(enqId);
    const lines = quotedLines(e);
    const rev = quotedRevisions(e);
    const hours = FC.budgetHours(lines) + FC.revTotalHours(lines, rev);
    const fee = FC.calcTotalFee(lines, rate) + FC.revFee(lines, rev, rate);
    const issued = e && e.feeCalc && e.feeCalc.issuedFeeOverride ? Number(e.feeCalc.totalFee) || null : null;
    return { e, lines, rev, hours, fee, feeToClient: issued != null ? issued : fee };
  };
  const draw = (body) => {
    const q = quote();
    const userOpt = `<option value="">— Unassigned —</option>${users.map((u) => `<option value="${escapeHtml(u.id)}"${u.id === lead ? ' selected' : ''}>${escapeHtml(u.name)}</option>`).join('')}`;
    const e = q.e;
    const enqCard = !e ? '<p class="icq-none">No enquiry is open for this client.</p>' : `
      ${candidates.length > 1 ? `<select class="f-select icq-enq" aria-label="Which enquiry">${candidates.map((x) => `<option value="${escapeHtml(x.id)}"${x.id === enqId ? ' selected' : ''}>${escapeHtml([x.number || 'Unnumbered', descFor(x)].filter(Boolean).join(' · ').slice(0, 90))}</option>`).join('')}</select>` : ''}
      <div class="icq-card">
        <div class="icq-card-h"><b>${escapeHtml(e.number || 'Enquiry')}</b><span>${escapeHtml([String(e.subject || descFor(e)).slice(0, 60), e.status === 'Won' && e.wonAt ? `won ${dayText(e.wonAt)}` : e.status ? e.status.toLowerCase() : ''].filter(Boolean).join(' · '))}</span>${q.lines.length ? '<span class="icq-tag">As quoted</span>' : ''}</div>
        ${q.lines.length ? q.lines.map((l) => `<div class="icq-row"><span>${escapeHtml(l.item)}${FC.lineIsComm(l) ? ' <span class="icq-int">internal</span>' : Number(l.qty) > 1 ? ` × ${escapeHtml(String(Number(l.qty)))}` : Number(l.complexity) && Number(l.complexity) !== 1 ? ` × ${escapeHtml(String(Number(l.complexity)))}` : ''}</span><span>${FC.lineIsComm(l) ? '—' : escapeHtml(H(FC.lineEffHours(l)))}</span><span>${escapeHtml(money(FC.lineEffFee(l, rate)))}</span></div>`).join('')
          + (Number(q.rev.count) > 0 ? `<div class="icq-row"><span>Revisions · ${escapeHtml(String(q.rev.count))} ${q.rev.charged ? 'charged' : 'included'}</span><span>${escapeHtml(H(FC.revTotalHours(q.lines, q.rev)))}</span><span>${q.rev.charged ? escapeHtml(money(FC.revFee(q.lines, q.rev, rate))) : '–'}</span></div>` : '')
          + `<div class="icq-row icq-tot"><span>${e.feeCalc && e.feeCalc.updatedAt ? `Quoted on ${escapeHtml(dayText(e.feeCalc.updatedAt))}` : 'As quoted'}</span><span>${escapeHtml(H(q.hours))}</span><span>${escapeHtml(money(q.feeToClient))}</span></div>`
          : '<p class="icq-none">This quote has no priced lines. Open it as a draft to price it.</p>'}
      </div>`;
    body.innerHTML = `
      <div class="icq">
        <div class="icq-l">Where it came from</div>
        <div class="icq-seg" role="tablist">
          <button type="button" role="tab" data-tab="enquiry" aria-selected="${tab === 'enquiry'}" class="${tab === 'enquiry' ? 'on' : ''}"${candidates.length ? '' : ' disabled'}>A won enquiry</button>
          <button type="button" role="tab" data-tab="direct" aria-selected="false">Client asked directly</button>
          <button type="button" role="tab" data-tab="later" aria-selected="${tab === 'later'}" class="${tab === 'later' ? 'on' : ''}">Price it later</button>
        </div>
        ${tab === 'enquiry' ? enqCard : '<p class="icq-later">Added unpriced, with a task for the lead to price it.</p>'}
        <label class="icq-f"><span class="icq-l">What’s been instructed</span><input class="f-input icq-desc" maxlength="500" value="${escapeHtml(desc)}" placeholder="What has been instructed?"></label>
        <div class="icq-ld">
          <label class="icq-f"><span class="icq-l">Lead</span><select class="f-select icq-lead">${userOpt}</select></label>
          <label class="icq-f"><span class="icq-lh"><span class="icq-l">Due</span><span class="icq-opt">Optional</span></span><input class="f-input icq-due" type="date" value="${escapeHtml(due)}"></label>
        </div>
      </div>`;
    body.querySelectorAll('[data-tab]').forEach((b) => b.addEventListener('click', () => {
      if (b.dataset.tab === 'direct') { handOff('direct'); return; }
      tab = b.dataset.tab; draw(body); syncFoot();
    }));
    body.querySelector('.icq-enq')?.addEventListener('change', (ev) => { enqId = ev.target.value; if (!desc || desc === descFor(q.e)) desc = descFor(candById.get(enqId)); draw(body); syncFoot(); });
    body.querySelector('.icq-desc').addEventListener('input', (ev) => { desc = ev.target.value; syncFoot(); });
    body.querySelector('.icq-lead').addEventListener('change', (ev) => { lead = ev.target.value; });
    body.querySelector('.icq-due').addEventListener('change', (ev) => { due = ev.target.value; });
  };
  const handOff = (kind) => {
    const q = quote();
    const prefill = { kind, description: desc, lead, due };
    if (kind === 'enquiry') Object.assign(prefill, { enquiryId: enqId, lines: q.lines, revisions: q.rev, ...(q.feeToClient !== q.fee ? { feeToClient: q.feeToClient } : {}) });
    if (d) d.close();
    if (onOpenDraft) onOpenDraft(prefill);
  };
  const syncFoot = () => {
    if (!d) return;
    const q = quote();
    const btn = d.foot.querySelector('.tf-btn-primary');
    if (btn) { btn.dataset.verb = tab === 'enquiry' ? `Add instruction ${seq} · ${money(q.feeToClient)}` : `Add instruction ${seq} · unpriced`; btn.dataset.working = 'Adding…'; }
    const ok = desc.trim() && (tab === 'later' || (q.e && q.lines.length));
    d.api.setPrimary(ok ? 'rest' : 'notyet');
  };
  d = openDialog({
    eyebrow: `${project.number || ''} · ${project.clientName || ''} · instruction ${seq}`.replace(/^ · /, ''),
    title: 'Add instruction',
    width: 640,
    cardClass: 'icq-dialog',
    render(body) { draw(body); },
    primary: {
      label: `Add instruction ${seq}`,
      onSubmit: async () => {
        if (!desc.trim()) throw new Error('Say what has been instructed.');
        const q = quote();
        const payload = { projectId, description: desc.trim(), lead: lead || null, due: due || null };
        let out;
        if (tab === 'enquiry') {
          if (!q.e || !q.lines.length) throw new Error('This quote has no priced lines — open it as a draft.');
          const below = q.feeToClient < q.fee;
          out = await createInstructionServer({ ...payload, source: { kind: 'enquiry', enquiryId: enqId }, lines: q.lines.map(({ asQuoted, ...l }) => l), revisions: q.rev,
            feeToClient: q.feeToClient, feeReason: below ? `As issued on the fee proposal for ${q.e.number || 'the enquiry'}` : null });
        } else {
          out = await createInstructionServer({ ...payload, source: { kind: 'later' } });
        }
        resumeIfHeld(projectId, session.email, session.displayName, 'an instruction was added').catch(() => {});
        toastV4(`Instruction ${(out && out.seq) || seq} added`);
        if (onDone) onDone();
      },
    },
  });
  // The footer's left: "Need to change the lines? Open as a draft" (page 4).
  const note = d.foot.querySelector('.tf-note');
  if (note) {
    note.innerHTML = 'Need to change the lines? <button type="button" class="icq-open">Open as a draft</button>';
    note.querySelector('.icq-open').addEventListener('click', () => handOff(tab === 'later' ? 'later' : 'enquiry'));
  }
  syncFoot();
  return d;
}
